import React, { useState } from 'react';
import { Tag, Save, Loader2, CheckCircle, AlertTriangle, Code2, Info } from 'lucide-react';
import {
  useTrackingSettings,
  TrackingSettings
} from '../../lib/hooks/useTrackingSettings';
import {
  extractTagId,
  findTagIdsInCode,
  getPreloadedTagIds,
  GA4_ID_PATTERN,
  GOOGLE_ADS_ID_PATTERN,
  GTM_ID_PATTERN,
  META_PIXEL_ID_PATTERN
} from '../../lib/tracking';

type FormData = Omit<TrackingSettings, 'id' | 'updatedAt'>;

const toFormData = (settings: TrackingSettings): FormData => ({
  enabled: settings.enabled,
  gtmContainerId: settings.gtmContainerId,
  ga4MeasurementId: settings.ga4MeasurementId,
  googleAdsId: settings.googleAdsId,
  metaPixelId: settings.metaPixelId,
  trackSpaPageViews: settings.trackSpaPageViews,
  customHeadCode: settings.customHeadCode,
  customBodyCode: settings.customBodyCode
});

const TrackingEditor = () => {
  const { settings, loading, error, saveSettings } = useTrackingSettings();
  const [formData, setFormData] = useState<FormData>(toFormData(settings));
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  // Тагове, заложени директно в index.html - четат се веднъж при отваряне.
  const [preloadedIds] = useState<string[]>(() => getPreloadedTagIds());

  React.useEffect(() => {
    if (!loading) {
      setFormData(toFormData(settings));
    }
  }, [settings, loading]);

  const handleIdChange = (
    field: keyof FormData,
    value: string,
    prefix: 'GTM' | 'G' | 'AW' | 'META'
  ) => {
    setFormData(prev => ({ ...prev, [field]: value ? extractTagId(value, prefix) : '' }));
  };

  const validate = (): string => {
    const { gtmContainerId, ga4MeasurementId, googleAdsId, metaPixelId } = formData;

    if (gtmContainerId && !GTM_ID_PATTERN.test(gtmContainerId)) {
      return 'Невалиден GTM Container ID. Форматът е GTM-XXXXXXX.';
    }
    if (ga4MeasurementId && !GA4_ID_PATTERN.test(ga4MeasurementId)) {
      return 'Невалиден GA4 Measurement ID. Форматът е G-XXXXXXXXXX.';
    }
    if (googleAdsId && !GOOGLE_ADS_ID_PATTERN.test(googleAdsId)) {
      return 'Невалиден Google Ads ID. Форматът е AW-000000000.';
    }
    if (metaPixelId && !META_PIXEL_ID_PATTERN.test(metaPixelId)) {
      return 'Невалиден Meta Pixel ID. Състои се само от цифри.';
    }
    return '';
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setSaveStatus({ type: null, message: '' });

    const validationError = validate();
    if (validationError) {
      setSaveStatus({ type: 'error', message: validationError });
      return;
    }

    setIsSaving(true);
    try {
      const success = await saveSettings(formData);
      setSaveStatus(
        success
          ? {
              type: 'success',
              message:
                'Настройките са запазени. Таговете се зареждат при следващото отваряне на сайта.'
            }
          : { type: 'error', message: 'Грешка при запазване на настройките' }
      );
    } catch (err) {
      console.error('Error saving tracking settings:', err);
      setSaveStatus({ type: 'error', message: 'Възникна неочаквана грешка' });
    } finally {
      setIsSaving(false);
    }
  };

  // ID-та, които вече се зареждат от index.html и затова се пропускат.
  const duplicateIds = [
    formData.gtmContainerId,
    formData.ga4MeasurementId,
    formData.googleAdsId
  ].filter(id => id && preloadedIds.includes(id));

  const configuredIds = [
    formData.gtmContainerId,
    formData.ga4MeasurementId,
    formData.googleAdsId,
    formData.metaPixelId,
    ...preloadedIds
  ].filter(Boolean);

  const customCodeDuplicates = Array.from(
    new Set([
      ...findTagIdsInCode(formData.customHeadCode),
      ...findTagIdsInCode(formData.customBodyCode)
    ])
  ).filter(id => configuredIds.includes(id));

  const hasNoscript = /<noscript/i.test(
    `${formData.customHeadCode} ${formData.customBodyCode}`
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Tag className="w-8 h-8 text-primary mr-3" />
          <div>
            <h1 className="text-2xl font-bold">Проследяване и тагове</h1>
            <p className="text-sm text-gray-600 mt-1">
              Google Tag Manager, Google Analytics, Google Ads и Facebook (Meta) Pixel - без
              промяна по кода на сайта
            </p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={isSaving}
          className="flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Запазване...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Запази промените
            </>
          )}
        </button>
      </div>

      {saveStatus.type && (
        <div
          className={`mb-6 p-4 rounded-lg border ${
            saveStatus.type === 'success'
              ? 'bg-green-50 text-green-800 border-green-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          <div className="flex items-center">
            {saveStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertTriangle className="w-5 h-5 mr-2" />
            )}
            <span>{saveStatus.message}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl">
        {/* Общ ключ */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Активно проследяване</h2>
              <p className="text-sm text-gray-600 mt-1">
                Изключете, за да спрете зареждането на всички тагове по-долу
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={e => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>

        {/* Google Tag Manager */}
        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
              Google Tag Manager
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Поставете Container ID или целия код от GTM - ID-то се разпознава автоматично
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              GTM Container ID
            </label>
            <input
              type="text"
              value={formData.gtmContainerId}
              onChange={e => handleIdChange('gtmContainerId', e.target.value, 'GTM')}
              placeholder="GTM-XXXXXXX"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono"
            />
            <p className="text-xs text-gray-500 mt-2">
              Намира се в Google Tag Manager горе вдясно, до името на контейнера.
            </p>
          </div>

          <div className="flex items-start gap-2 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p>
              Сайтът е Single Page Application. При смяна на страница се изпраща събитие{' '}
              <code className="font-mono bg-white px-1 rounded">page_view</code> в dataLayer с{' '}
              <code className="font-mono bg-white px-1 rounded">page_path</code>,{' '}
              <code className="font-mono bg-white px-1 rounded">page_location</code> и{' '}
              <code className="font-mono bg-white px-1 rounded">page_title</code>. Използвайте го
              като Custom Event trigger в GTM.
            </p>
          </div>
        </div>

        {/* Google тагове без GTM */}
        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
              Google тагове (без GTM)
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Попълнете само ако таговете не се управляват през Google Tag Manager
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Analytics 4 (Measurement ID)
              </label>
              <input
                type="text"
                value={formData.ga4MeasurementId}
                onChange={e => handleIdChange('ga4MeasurementId', e.target.value, 'G')}
                placeholder="G-XXXXXXXXXX"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono"
              />
              <p className="text-xs text-gray-500 mt-2">
                Google Analytics - Admin - Data Streams - вашият поток.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Ads (Conversion ID)
              </label>
              <input
                type="text"
                value={formData.googleAdsId}
                onChange={e => handleIdChange('googleAdsId', e.target.value, 'AW')}
                placeholder="AW-000000000"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono"
              />
            </div>
          </div>

          {preloadedIds.length > 0 && (
            <div
              className={`flex items-start gap-2 p-4 rounded-lg text-sm border ${
                duplicateIds.length > 0
                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-gray-50 border-gray-200 text-gray-700'
              }`}
            >
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p>
                  Вече заложени директно в кода на сайта (index.html):{' '}
                  <span className="font-mono font-semibold">{preloadedIds.join(', ')}</span>
                </p>
                {duplicateIds.length > 0 && (
                  <p className="mt-1 font-medium">
                    {duplicateIds.join(', ')} вече се зарежда от кода и няма да бъде добавен втори
                    път, за да не се брои двойно.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="text-base font-semibold text-gray-800">
                Проследяване на навигацията
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Изпраща page_view при смяна на страница без презареждане
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.trackSpaPageViews}
                onChange={e =>
                  setFormData(prev => ({ ...prev, trackSpaPageViews: e.target.checked }))
                }
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>

        {/* Meta Pixel */}
        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
              Facebook (Meta) Pixel
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Поставете Pixel ID или целия код от Meta Events Manager
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Pixel ID</label>
            <input
              type="text"
              value={formData.metaPixelId}
              onChange={e => handleIdChange('metaPixelId', e.target.value, 'META')}
              placeholder="1234567890123456"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono"
            />
            <p className="text-xs text-gray-500 mt-2">
              Meta Events Manager - Data Sources - вашият pixel (само цифри).
            </p>
          </div>

          <div className="flex items-start gap-2 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Събития, които се изпращат автоматично:</p>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>
                  <code className="font-mono bg-white px-1 rounded">PageView</code> - при
                  зареждане и при всяка смяна на страница
                </li>
                <li>
                  <code className="font-mono bg-white px-1 rounded">Lead</code> - при успешно
                  изпратена форма за контакт
                </li>
              </ul>
              <p className="mt-2">
                Същите събития отиват и в dataLayer (
                <code className="font-mono bg-white px-1 rounded">page_view</code>,{' '}
                <code className="font-mono bg-white px-1 rounded">generate_lead</code>), така че
                могат да се ползват и от GTM.
              </p>
            </div>
          </div>
        </div>

        {/* Допълнителен код */}
        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 flex items-center">
              <Code2 className="w-5 h-5 text-primary mr-2" />
              Допълнителен код
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              За тагове без собствено поле по-горе - LinkedIn Insight, TikTok Pixel, верификация
              на домейн и подобни
            </p>
          </div>

          {customCodeDuplicates.length > 0 && (
            <div className="flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">
                  Кодът съдържа{' '}
                  <span className="font-mono">{customCodeDuplicates.join(', ')}</span>, което вече
                  се зарежда от полетата по-горе.
                </p>
                <p className="mt-1">
                  Тази част няма да бъде изпълнена, за да не се брои двойно. Може да я премахнете
                  оттук - достатъчно е ID-то в полето.
                </p>
              </div>
            </div>
          )}

          {hasNoscript && (
            <div className="flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>
                Кодът съдържа{' '}
                <code className="font-mono bg-white px-1 rounded">&lt;noscript&gt;</code> блок. Той
                се пропуска - има смисъл само при изключен JavaScript, а кодът тук се зарежда
                именно от JavaScript.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Код в &lt;head&gt;
            </label>
            <textarea
              value={formData.customHeadCode}
              onChange={e => setFormData(prev => ({ ...prev, customHeadCode: e.target.value }))}
              rows={8}
              spellCheck={false}
              placeholder="<script>...</script>"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Код в началото на &lt;body&gt;
            </label>
            <textarea
              value={formData.customBodyCode}
              onChange={e => setFormData(prev => ({ ...prev, customBodyCode: e.target.value }))}
              rows={6}
              spellCheck={false}
              placeholder="<script>...</script>"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono text-sm"
            />
          </div>

          <div className="flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p>
              Кодът тук се изпълнява на всяка публична страница. Поставяйте само код от доверен
              източник - грешен код може да счупи сайта.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TrackingEditor;
